import { Badge, Dropdown, Empty } from 'antd';
import IntlMessages from '@nxsys/helpers/IntlMessages';
import NotificationItem from './NotificationItem';
import Icon from '@nxsys/components/AppIconImage';
import { IconBell } from '@nxsys/assets/icons';
import {
  StyledDrowdownWrapper,
  StyledNotifyButtonAll,
  StyledNotifyHeader,
  StyledNotifyIcon,
  StyledNotifyMarkAllRead,
  StyledNotifyLink,
  StyledNotifyList,
  StyledNotifyScrollSubmenu,
  StyledNotifyText,
} from './index.styled';
import { useSelector, useDispatch } from 'react-redux';
import type { AppState, AppDispatch } from '@nxsys/store';
import { notificationActions } from '@nxsys/store/slices/notification/reducer/notificationSlice';
import { getNotificationRoute } from './notificationRoutes';
import { downloadFile } from '@nxsys/helpers/FileHelper';
import { isPayslipZipNotification } from '@nxsys/utils/payslip-zip-download';
import { isReportExportNotification } from '@nxsys/utils/report-export-download';
import { IRELAND } from '@nxsys/store/constants/keys';
import { useNavigateWithQuery } from '@nxsys/hooks/useNavigateWithQuery';
import { downloadReportExport } from '../../../modules/reportBuilder/exportDownload';

const AppNotifications = () => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigateWithQuery();
  const { data: notifications = [], totalUnread = 0 } = useSelector(
    (state: AppState) => state.notificationStore?.notificationList || {},
  );
  const { user, isEmployee, isAgency, payrollCountry } = useSelector(
    (state: AppState) => state.appStore,
  );

  // NX-5877: opening the bell used to mark every notification read on the
  // spot, without the user asking. Now only "Mark all as read" does that, or
  // opening one notification marks that one.
  const handleMarkAllRead = () => {
    dispatch(
      notificationActions.markAllReadNotification({
        role: (user as any)?.role,
        userId: (user as any)?.id,
        subcontractorId: (user as any)?.subcontractorId?.id,
        agencyId: (user as any)?.agencyId?.id,
      }),
    );
  };

  const handleNotificationClick = (item: any) => {
    dispatch(notificationActions.markReadNotification({ id: item.id }));
    // NX-5603: a finished Download All opens its ZIP.
    if (isPayslipZipNotification(item)) return downloadFile(item.urlFile);
    if (isReportExportNotification(item, (user as any)?.id)) {
      return downloadReportExport(item.fromId);
    }
    const route = getNotificationRoute(item, {
      isEmployee,
      isAgency,
      isIreland: payrollCountry === IRELAND,
    });
    if (route) navigate(route);
  };

  const items = [
    {
      key: 1,
      label: (
        <StyledNotifyHeader className="header">
          <span>
            <IntlMessages id="common.notifications" />
            {totalUnread > 0 ? ` (${totalUnread})` : ''}
          </span>
          {totalUnread > 0 && (
            <StyledNotifyMarkAllRead
              type="link"
              size="small"
              onClick={handleMarkAllRead}
            >
              <IntlMessages id="common.markAllAsRead" />
            </StyledNotifyMarkAllRead>
          )}
        </StyledNotifyHeader>
      ),
    },
    {
      key: 2,
      label: (
        <StyledNotifyScrollSubmenu>
          {notifications.length > 0 ? (
            <StyledNotifyList
              dataSource={notifications.slice(0, 20)}
              renderItem={(item: any) => (
                <NotificationItem
                  key={item.id}
                  item={item}
                  userId={(user as any)?.id}
                  onClick={() => handleNotificationClick(item)}
                />
              )}
            />
          ) : (
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description="No notifications"
              style={{ padding: '20px 0' }}
            />
          )}
        </StyledNotifyScrollSubmenu>
      ),
    },
    {
      key: 3,
      label: (
        <StyledNotifyButtonAll
          type="text"
          onClick={() => navigate('/notifications')}
        >
          <IntlMessages id="common.viewAll" />
        </StyledNotifyButtonAll>
      ),
    },
  ];

  return (
    <StyledDrowdownWrapper>
      <Dropdown
        menu={{ items }}
        className="dropdown"
        overlayClassName="header-notify-messages"
        getPopupContainer={(triggerNode) => triggerNode}
        trigger={['click']}
      >
        <StyledNotifyLink onClick={(e) => e.preventDefault()}>
          <StyledNotifyIcon className={totalUnread > 0 ? 'header-icon-has-unread' : ''}>
            <Badge count={totalUnread} size="small" offset={[-2, 2]}>
              <Icon src={IconBell} alt="Notifications" />
            </Badge>
          </StyledNotifyIcon>
          <StyledNotifyText>
            <IntlMessages id="common.notifications" />
          </StyledNotifyText>
        </StyledNotifyLink>
      </Dropdown>
    </StyledDrowdownWrapper>
  );
};

export default AppNotifications;
